import React, { ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Carousel } from 'antd'
import { collection, getDocs } from 'firebase/firestore/lite'
import { navigate } from 'gatsby'

import { Trans } from 'react-i18next'
import {
  ContentContainer,
  SectionWrapper,
} from '~components/homepage/styled/shared'

import { mediaQueries, palette } from '~components/homepage/styled/constants'
import { StoreItem } from '~src/models/store'
import { storeDb } from '~src/api/store-api'
import { SimpleStoreItem } from '~components/store'
import { ArrowSvg } from '~components/homepage/svg/icons/arrow'
import { BranchTitle } from '~components/homepage/styled/grid-materials'
import { SVGImage } from '~components/homepage/svg/SVGImage'
import { KlosySVGId } from '~components/homepage/svg/types/svg'
import useIsMobile from '~src/utils/useIsMobile'
import posthog from '~src/analytics/posthog'

const StoreSectionWrapper = styled(SectionWrapper)`
  background: ${palette.smokeWhite};
  padding: 80px 0;

  @media (max-width: ${mediaQueries.small}) {
    padding: 40px 0;
  }
`

const CarouselWrapper = styled.div`
  padding: 0px 10%;
  height: 450px;

  position: relative;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    width: 100px;
    height: 100%;
    z-index: 10;
    pointer-events: none;
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, ${palette.smokeWhite}, transparent);
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, ${palette.smokeWhite}, transparent);
  }

  @media (max-width: 768px) {
    &::before,
    &::after {
      display: none;
    }
  }

  .slick-list {
    overflow: unset;
  }

  .slick-slide {
    padding: 0 25px;
    transition: all 0.3s ease;
  }

  .slick-center.slick-slide {
    transform: scale(1.3);
  }

  .slick-track {
    display: flex;
    align-items: center;
  }

  .slick-slide > div {
    height: 100%;
  }
`

const StyledNextArrow = styled.span`
  width: 11px;
  right: -7px !important;
  transform: rotate(180deg);
  transform-origin: 50% 50%;
  z-index: 100;
  top: 50% !important;
  transition: 0.3s;

  svg {
    width: 11px;
    fill: white;
  }

  &::before {
    display: none;
  }

  @media (max-width: ${mediaQueries.small}) {
    right: -10px !important;
  }
`

const StyledPrevArrow = styled.span`
  left: -7px !important;
  width: 11px;
  z-index: 100;
  transform: none !important;
  transition: 0.3s;

  svg {
    width: 11px;
    stroke: white;
  }

  &::before {
    display: none;
  }

  @media (max-width: ${mediaQueries.small}) {
    left: -10px !important;
  }
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: ${palette.darkBiege};
`

function NextArrow({ className, onClick }: any): ReactElement {
  const handleClick = (e: any) => {
    posthog.capture('store_carousel_arrow_clicked', {
      action: 'next',
      section: 'homepage',
    })
    onClick(e)
  }

  return (
    <StyledNextArrow onClick={handleClick} className={className}>
      <ArrowSvg />
    </StyledNextArrow>
  )
}

function PrevArrow({ className, style, onClick }: any): ReactElement {
  const handleClick = (e: any) => {
    posthog.capture('store_carousel_arrow_clicked', {
      action: 'prev',
      section: 'homepage',
    })
    onClick(e)
  }

  return (
    <StyledPrevArrow
      onClick={handleClick}
      className={className}
      style={{ ...style }}
    >
      <ArrowSvg />
    </StyledPrevArrow>
  )
}

const StoreSection = (): ReactElement => {
  const [storeItems, setStoreItems] = useState<StoreItem[]>([])
  const [loading, setLoading] = useState(true)
  const isMobile = useIsMobile()

  const fetchStoreItems = async (): Promise<void> => {
    try {
      const store = collection(storeDb, 'store')
      const items = await getDocs(store)
      const parsedItems = items.docs.map(doc => doc.data()) as StoreItem[]

      const limitedItems = parsedItems.slice(0, 6)
      setStoreItems(limitedItems)
    } catch (error) {
      console.error('Error fetching store items:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleItemClick = (item: StoreItem): void => {
    const itemIndex = storeItems.findIndex(i => i.id === item.id)
    posthog.capture('store_item_clicked', {
      item_id: item.id,
      knife: item.knife,
      metal: item.metal,
      wood: item.wood,
      price: item.price?.[0] || '0',
      position: itemIndex,
      section: 'homepage_carousel',
    })

    // Переходим на страницу store с query параметром
    navigate(`/store?item=${item.id}`)
  }

  useEffect(() => {
    fetchStoreItems()
  }, [])

  return (
    <StoreSectionWrapper>
      <ContentContainer maxWidth="1125px">
        <BranchTitle>
          <SVGImage svgProps={[KlosySVGId.klosSide, 'left-branch', 68, 10]} />
          <span>IN STOCK</span>
          <SVGImage svgProps={[KlosySVGId.klosSide, 'right-branch', 68, 10]} />
        </BranchTitle>

        <div style={{ height: '60px' }} />

        {loading ? (
          <LoadingWrapper>
            <Trans i18nKey="store.loading">...</Trans>
          </LoadingWrapper>
        ) : (
          storeItems.length > 0 && (
            <CarouselWrapper>
              <Carousel
                dots={false}
                infinite
                autoplay={false}
                speed={500}
                slidesToShow={isMobile ? 1 : 3}
                slidesToScroll={1}
                centerMode
                centerPadding="0px"
                nextArrow={<NextArrow />}
                prevArrow={<PrevArrow />}
                arrows
              >
                {storeItems.map(item => (
                  <div key={item.id}>
                    <SimpleStoreItem {...item} onItemClick={handleItemClick} />
                  </div>
                ))}
              </Carousel>
            </CarouselWrapper>
          )
        )}
      </ContentContainer>
    </StoreSectionWrapper>
  )
}

export default StoreSection
